/**
 * GameHistoryScreen — 對戰歷史列表
 *
 * 顯示玩家的對戰歷史記錄，可以點擊觀看重播。
 */

import { useEffect, useState, useCallback } from 'react';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { useReplayStore } from '../../store/replayStore';
import { getGameHistory, type GameHistory } from '../../services/NetworkManager';

const WIN_CONDITION_MAP: Record<string, string> = {
  'score': '分數勝利',
  'countdown': '倒數結束',
  'deckout': '牌庫耗盡',
  'surrender': '投降',
  'disconnect': '斷線',
};

function formatDuration(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m)}:${String(s).padStart(2, '0')}`;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return `${String(d.getMonth() + 1)}/${String(d.getDate())} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return dateStr;
  }
}

export function GameHistoryScreen() {
  const { navigateTo, goBack } = useUIStore();
  const { token, player } = useAuthStore();
  const { loadReplay } = useReplayStore();

  const [games, setGames] = useState<GameHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const fetchHistory = useCallback(async (pageOffset: number) => {
    if (!token) {
      setError('請先登入');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const resp = await getGameHistory(token, pageOffset, limit);
      setGames(resp.games);
      setTotalCount(resp.total_count);
      setOffset(pageOffset);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void fetchHistory(0);
  }, [fetchHistory]);

  const handleReplay = useCallback(async (gameId: string) => {
    await loadReplay(gameId);
    navigateTo('replay');
  }, [loadReplay, navigateTo]);

  const handlePrevPage = useCallback(() => {
    const newOffset = Math.max(0, offset - limit);
    void fetchHistory(newOffset);
  }, [offset, fetchHistory]);

  const handleNextPage = useCallback(() => {
    const newOffset = offset + limit;
    if (newOffset < totalCount) {
      void fetchHistory(newOffset);
    }
  }, [offset, totalCount, fetchHistory]);

  const playerId = player?.id ?? '';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={goBack}>← 返回</button>
        <h1 style={styles.title}>對戰歷史</h1>
        <span style={styles.countText}>共 {totalCount} 場</span>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {isLoading && (
          <div style={styles.centerMessage}>⏳ 載入中...</div>
        )}

        {error && (
          <div style={styles.errorMessage}>❌ {error}</div>
        )}

        {!isLoading && !error && games.length === 0 && (
          <div style={styles.centerMessage}>尚無對戰記錄</div>
        )}

        {games.map((game) => {
          const isWinner = game.winner_id === playerId;
          const isDraw = game.winner_id === null;
          const isP1 = game.player1_id === playerId;
          const opponentId = isP1 ? game.player2_id : game.player1_id;
          const mmrChange = isP1 ? game.p1_mmr_change : game.p2_mmr_change;

          return (
            <div key={game.id} style={styles.gameItem}>
              <div style={styles.gameLeft}>
                <div style={styles.gameResult}>
                  <span style={{
                    ...styles.resultBadge,
                    backgroundColor: isDraw ? '#4a4a3a' : isWinner ? '#3a5a3a' : '#5a3a3a',
                    color: isDraw ? '#cccc88' : isWinner ? '#88ff88' : '#ff8888',
                  }}>
                    {isDraw ? '🤝 平手' : isWinner ? '👑 勝利' : '💀 失敗'}
                  </span>
                </div>
                <div style={styles.gameInfo}>
                  <span style={styles.opponentText}>
                    vs {opponentId.slice(0, 8)}
                  </span>
                  <span style={styles.detailText}>
                    {WIN_CONDITION_MAP[game.win_condition] ?? game.win_condition}
                    {' · '}{formatDuration(game.duration_secs)}
                    {' · '}{game.total_turns} 回合
                    {mmrChange !== 0 && (
                      <span style={{ color: mmrChange > 0 ? '#88ff88' : '#ff8888' }}>
                        {' · MMR '}{mmrChange > 0 ? '+' : ''}{mmrChange}
                      </span>
                    )}
                  </span>
                  <span style={styles.dateText}>{formatDate(game.created_at)}</span>
                </div>
              </div>
              <button
                style={styles.replayButton}
                onClick={() => { void handleReplay(game.game_id); }}
              >
                📺 觀看重播
              </button>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalCount > limit && (
        <div style={styles.pagination}>
          <button
            style={{
              ...styles.pageButton,
              opacity: offset <= 0 ? 0.4 : 1,
            }}
            onClick={handlePrevPage}
            disabled={offset <= 0}
          >
            ◀ 上一頁
          </button>
          <span style={styles.pageInfo}>
            {offset + 1}-{Math.min(offset + limit, totalCount)} / {totalCount}
          </span>
          <button
            style={{
              ...styles.pageButton,
              opacity: offset + limit >= totalCount ? 0.4 : 1,
            }}
            onClick={handleNextPage}
            disabled={offset + limit >= totalCount}
          >
            下一頁 ▶
          </button>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100vw',
    height: '100vh',
    backgroundColor: '#1a1a2e',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Arial, sans-serif',
    color: '#ffffff',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#252545',
    borderBottom: '1px solid #3a3a5a',
    gap: '16px',
    flexShrink: 0,
  },
  backButton: {
    padding: '8px 16px',
    fontSize: '14px',
    backgroundColor: '#3a3a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  countText: {
    marginLeft: 'auto',
    fontSize: '14px',
    color: '#888888',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  centerMessage: {
    textAlign: 'center',
    color: '#888888',
    padding: '60px 0',
    fontSize: '16px',
  },
  errorMessage: {
    textAlign: 'center',
    color: '#ff8888',
    padding: '40px 0',
    fontSize: '16px',
  },
  gameItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#2a2a4a',
    borderRadius: '8px',
    gap: '16px',
  },
  gameLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  gameResult: {
    flexShrink: 0,
  },
  resultBadge: {
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  gameInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  opponentText: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  detailText: {
    fontSize: '12px',
    color: '#aaaacc',
  },
  dateText: {
    fontSize: '11px',
    color: '#666688',
  },
  replayButton: {
    padding: '8px 16px',
    fontSize: '13px',
    backgroundColor: '#3a5a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    padding: '12px 24px',
    backgroundColor: '#252545',
    borderTop: '1px solid #3a3a5a',
    flexShrink: 0,
  },
  pageButton: {
    padding: '6px 14px',
    fontSize: '13px',
    backgroundColor: '#3a3a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  pageInfo: {
    fontSize: '13px',
    color: '#888888',
  },
};
